import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type Control } from '../lib/api.ts';
import { Badge } from './ui/Button.tsx';

type Framework = { id: string; code: string; name: string; version: string };

/**
 * 통제항목 선택기 (CR-035). 검색(코드·제목) · 프레임워크 탭 · 장·절 머리글이 있는 체크박스 목록 · 상단 선택 칩.
 * 목록은 GET /controls 전체를 1회 로드해 클라이언트에서 거른다(운영 129건). 다중 선택 상자(Ctrl 클릭)를 대체한다.
 */
export function ControlPicker({ value, onChange, disabled, maxHeight = '24rem' }: { value: string[]; onChange: (ids: string[]) => void; disabled?: boolean; maxHeight?: string }) {
  const controls = useQuery({ queryKey: ['controls', '', ''], queryFn: () => api<Control[]>('/controls') });
  const frameworks = useQuery({ queryKey: ['frameworks'], queryFn: () => api<Framework[]>('/frameworks') });
  const [q, setQ] = useState('');
  const [fw, setFw] = useState<string>('');
  const fwById = useMemo(() => Object.fromEntries((frameworks.data ?? []).map((f) => [f.id, f])), [frameworks.data]);
  const selected = useMemo(() => new Set(value), [value]);
  const byId = useMemo(() => Object.fromEntries((controls.data ?? []).map((c) => [c.id, c])), [controls.data]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (controls.data ?? []).filter((c) => (!fw || c.frameworkId === fw) && (!needle || c.code.toLowerCase().includes(needle) || c.title.toLowerCase().includes(needle)));
  }, [controls.data, q, fw]);

  // 프레임워크 → 그룹(장·절) → 항목 순서 유지
  const groups = useMemo(() => {
    const out: Array<{ key: string; label: string; items: Control[] }> = [];
    let cur: { key: string; label: string; items: Control[] } | null = null;
    for (const c of filtered) {
      const f = fwById[c.frameworkId];
      const key = `${c.frameworkId}:${c.groupCode ?? ''}`;
      if (!cur || cur.key !== key) { cur = { key, label: `${f ? f.code : ''}${c.groupCode ? ` · ${c.groupCode}` : ''}${c.groupTitle ? ` ${c.groupTitle}` : ''}`, items: [] }; out.push(cur); }
      cur.items.push(c);
    }
    return out;
  }, [filtered, fwById]);

  const toggle = (id: string) => { if (disabled) return; onChange(selected.has(id) ? value.filter((v) => v !== id) : [...value, id]); };
  const visibleIds = filtered.map((c) => c.id);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  return (
    <div className="border border-slate-300 bg-white ft-w-full">
      {/* 선택 칩 */}
      <div className="flex flex-wrap items-center gap-1 px-2 py-1 border-b border-slate-200 bg-slate-50 min-h-[28px]">
        <span className="text-[11.5px] text-slate-500 mr-1">선택 {value.length}개</span>
        {value.map((id) => { const c = byId[id]; return (
          <span key={id} className="inline-flex items-center gap-1 h-5 border border-sky-200 bg-white px-1.5 text-[11.5px] text-sky-900">
            <span className="font-mono">{c?.code ?? id.slice(0, 8)}</span><span className="max-w-[14rem] truncate">{c?.title ?? ''}</span>
            {!disabled && <button type="button" className="text-slate-400 hover:text-red-600" aria-label="제거" onClick={() => toggle(id)}>×</button>}
          </span>
        ); })}
        {value.length > 0 && !disabled && <button type="button" className="ml-auto text-[11.5px] text-slate-500 hover:text-accent-strong hover:underline" onClick={() => onChange([])}>모두 지우기</button>}
      </div>
      {/* 검색 · 프레임워크 탭 */}
      <div className="flex items-center gap-1 px-2 py-1 border-b border-slate-200">
        <input className="ft-control ft-w-sm" placeholder="코드·제목 검색" value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} />
        <div className="flex items-center gap-0.5 ml-1 text-[11.5px]">
          <button type="button" className={`h-6 px-2 border ${fw === '' ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} onClick={() => setFw('')}>전체</button>
          {(frameworks.data ?? []).map((f) => <button key={f.id} type="button" title={`${f.name} ${f.version}`} className={`h-6 px-2 border ${fw === f.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`} onClick={() => setFw(f.id)}>{f.code}</button>)}
        </div>
        <span className="ml-auto text-[11.5px] text-slate-500">{filtered.length}건</span>
        {!disabled && filtered.length > 0 && <button type="button" className="text-[11.5px] text-slate-500 hover:text-accent-strong hover:underline" onClick={() => onChange(allVisibleSelected ? value.filter((v) => !visibleIds.includes(v)) : [...value, ...visibleIds.filter((id) => !selected.has(id))])}>{allVisibleSelected ? '보이는 항목 해제' : '보이는 항목 모두 선택'}</button>}
      </div>
      {/* 목록 */}
      <div className="overflow-y-auto" style={{ maxHeight }}>
        {controls.isLoading && <div className="px-2 py-2 text-[12px] text-slate-500">불러오는 중…</div>}
        {!controls.isLoading && groups.length === 0 && <div className="px-2 py-2 text-[12px] text-slate-500">해당하는 통제항목이 없습니다</div>}
        {groups.map((g) => (
          <div key={g.key}>
            <div className="sticky top-0 px-2 h-6 leading-6 bg-slate-100 border-y border-slate-200 text-[11.5px] font-semibold text-slate-600">{g.label}</div>
            {g.items.map((c) => (
              <label key={c.id} className={`flex items-center gap-2 h-6 px-2 text-[12.5px] cursor-pointer hover:bg-sky-50 ${selected.has(c.id) ? 'bg-sky-50' : ''}`}>
                <input type="checkbox" className="accent-accent" checked={selected.has(c.id)} disabled={disabled} onChange={() => toggle(c.id)} />
                <span className="font-mono text-slate-500 w-[5.5rem] shrink-0">{c.code}</span>
                <span className="truncate">{c.title}</span>
                {c.checkCycle && c.checkCycle !== 'none' && <Badge tone="neutral">{c.checkCycle}</Badge>}
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
